export default {
  en: {
    memoryNotKept: "I couldn't save what you told me this time, so I may not remember it next time. You can tell me again.",
    team: {
      nowHelping: "Now helping: {{member}}",
      why: "Why",
      answered: "{{member}} answered",
      members: "Team members",
      asked: "Asked {{member}}",
      helped: "{{member}} helped",
      request: "Asked: {{text}}",
      reason: "Why: {{text}}",
      answer: "{{member}} answered: {{text}}",
      refused: "{{member}} was not allowed to help: {{reason}}",
      failed: "{{member}} could not finish: {{reason}}",
      status: { running: "Working", completed: "Done", failed: "Did not finish", refused: "Not allowed", cancelled: "Stopped" },
    },
    title: "Assistant",
    correction: {
      ack: "Got it — switching to your correction.",
      cutOff: "Stopped: replaced by your correction",
      readCutOff: "Reading aloud stopped when you started talking",
      hint: "You can send a correction while the agent is working: it stops after the current step and starts again with your correction.",
      done: "Before your correction the agent had already done: {{steps}}.",
      changed: "These changed something and were not undone: {{steps}}. Ask if you want them reversed.",
    },
    problems: {
      title: "Some steps did not work, so the answer may be incomplete:",
      denied: "“{{step}}” was not allowed.",
      failed: "“{{step}}” did not work.",
      rejected: "“{{step}}” was rejected by the approver.",
      comment: "Their comment: {{comment}}",
    },
    claims: {
      title: "Note: the answer says something was done, but no system did it in this request:",
      case: "No case or ticket was actually opened.",
      notify: "No message or notification was actually sent.",
      payment: "No payment was actually made.",
      access: "No access was actually granted.",
      booking: "Nothing was actually booked.",
    },
    runNotes: {
      limitWent: "“{{step}}” reached its limit of {{limit}} runs, so the agent went on to “{{to}}”.",
      limitStopped: "“{{step}}” reached its limit of {{limit}} runs, so the run stopped and kept the last answer.",
      simulated: "The AI was not sure ({{pct}} sure). In a safe test it went on with its own suggestion: {{value}}.",
      retried: "“{{step}}” didn't work, so it was tried again (try {{attempt}}).",
      workingNotes: "Working notes",
      workingNotesHelp: "What the steps wrote for each other along the way. The person sees only the answer.",
    },
    subtitle: "Ask an agent; you see each step it takes, the systems it uses and any approval, as it happens.",
    agent: "Agent",
    conversation: "Conversation",
    empty: "No messages yet. Type a question below to get started.",
    input: "Message",
    placeholder: "Type your question… (Enter to send, Shift+Enter for a new line)",
    send: "Send",
    newConversation: "New conversation",
    download: "Download {{name}}",
    newConversationHint: "Start again: the agent forgets the earlier questions and attached documents of this conversation.",
    remembers_one: "The agent remembers 1 earlier question of this conversation.",
    remembers_other: "The agent remembers {{count}} earlier questions of this conversation.",
    cancel: "Stop run",
    runError: "The run failed",
    role: { user: "You", assistant: "Agent" },
    status: { idle: "Ready", running: "Running", awaiting_approval: "Awaiting approval", awaiting_input: "Waiting for your answer", finished: "Finished", error: "Failed", cancelled: "Cancelled" },
    classification: { public: "Public", internal: "Internal", confidential: "Confidential", restricted: "Restricted" },
    upload: {
      button: "Attach a document",
      attached: "Attached documents",
      done: "{{name}} uploaded and classified",
      failed: "Upload failed",
      processing: "analysing…",
      hint: "You can attach PDF, Word, Excel, web pages, text or images (PNG, JPEG, TIFF), up to 25 MB.",
      remove: "Remove {{name}}",
      kind: "Kind of document {{name}} (change it if it is wrong)",
      willRead: "Will be read by the agent",
      read_one: "Read by the agent (1 page)",
      read_other: "Read by the agent ({{count}} pages)",
      readPartly: "Only the first {{read}} of {{pages}} pages were read",
      notRead: "The agent could not read {{name}}",
      refused: {
        too_sensitive: "Not read: too sensitive for this agent",
        not_found: "Not read: the document was not found",
        unreadable: "Not read: no text could be read from it",
        processing: "Not read yet: it is still being analysed. Ask again in a moment.",
      },
      reason: {
        too_large: "{{name}} is larger than 25 MB.",
        unsupported: "{{name}}: this type of file is not accepted.",
        encrypted: "{{name}} is password-protected. Attach a copy without a password.",
        unsafe: "{{name}} contains macros or active content and was refused.",
        empty: "{{name}} is empty.",
        unreadable: "{{name}} could not be read. Check that the file is not damaged.",
        network: "{{name}} could not be sent: check your connection and try again.",
        failed: "{{name}} could not be uploaded. Try again.",
      },
    },
    docLabel: { invoice: "Invoice", purchase_order: "Purchase order", contract: "Contract", id_document: "ID document", other: "Other" },
    activity: {
      title: "Activity",
      empty: "The agent's steps and the systems it uses appear here while it works.",
      steps: "Steps",
      tools: "Systems used",
      noTools: "No systems used yet",
      node: "Step: {{node}}",
      readDocument_one: "Read the attached document (1 page)",
      readDocument_other: "Read the attached document ({{count}} pages)",
      readDocumentPartly: "Read the attached document: only the first {{read}} of {{pages}} pages",
      usedConversation_one: "Used 1 earlier question of the conversation",
      usedConversation_other: "Used {{count}} earlier questions of the conversation",
    },
    steps: { working: "Working", retrieve: "Searching knowledge", think: "Reasoning", lookup: "Looking up records", approve: "Waiting for approval", pay: "Scheduling payment", done: "Done" },
    citations: { title: "Sources", empty: "No sources were retrieved for this run.", source: "Source: {{source}}", score: "Relevance: {{score}}", uri: "Source location" },
    approval: {
      title: "Approval required",
      answered: "Answer sent",
      args: "Proposed tool arguments",
      comment: "Comment (required to reject)",
      approve: "Approve",
      reject: "Reject",
      open: "Open in reviewer inbox",
      waiting: "Waiting for an approver. You will see the decision here.",
      approved: "Approved and signed",
      rejected: "Rejected and signed",
      status: { pending: "Pending", decided: "Decided", invalid_signature: "Invalid signature" },
      error: {
        validation: "Check the form: {{message}}",
        sign: "The approval could not be signed: {{message}}",
        decision: "Signed, but not yet applied: {{message}}. Retry from the inbox.",
      },
    },
    memory: {
      open: "Memory",
      title: "My memory",
      consent: "Allow {{agent}} to remember me",
      consentHelp: "Without consent the agent keeps nothing about you after the run. Revoking stops new memories; you can also delete existing ones.",
      consentGranted: "Consent granted",
      consentRevoked: "Consent revoked",
      export: "Export",
      deleteAll: "Delete all",
      deleteAllConfirm: "Delete every memory about you? This cannot be undone.",
      deleteConfirm: "Delete this memory?",
      deleteOne: "Delete memory",
      delete: "Delete",
      cancel: "Cancel",
      empty: "No memories stored.",
      expires: "Expires {{date}}",
      kind: { working: "From this conversation", episodic: "From past conversations", semantic: "A fact about you", procedural: "How you like things done" },
      listTitle: "Things I remember about you",
      anyAgent: "Any assistant",
      exportCsv: "Download as a spreadsheet (CSV)",
      exportJson: "Download all data (JSON)",
      csv: { kind: "Type", what: "What is remembered", agent: "Assistant", saved: "Saved on", expires: "Forgotten on" },
    },
  },
  ar: {
    memoryNotKept: "تعذّر حفظ ما أخبرتني به هذه المرة، لذا قد لا أتذكره في المرة القادمة. يمكنك إخباري مرة أخرى.",
    team: {
      nowHelping: "يساعدك الآن: {{member}}",
      why: "لماذا",
      answered: "أجاب {{member}}",
      members: "أعضاء الفريق",
      asked: "سُئل {{member}}",
      helped: "ساعد {{member}}",
      request: "السؤال: {{text}}",
      reason: "السبب: {{text}}",
      answer: "أجاب {{member}}: {{text}}",
      refused: "لم يُسمح لـ{{member}} بالمساعدة: {{reason}}",
      failed: "لم يتمكن {{member}} من الإنهاء: {{reason}}",
      status: { running: "يعمل", completed: "تم", failed: "لم يكتمل", refused: "غير مسموح", cancelled: "توقف" },
    },
    title: "المساعد",
    correction: {
      ack: "فهمت — سأنتقل إلى تصحيحك.",
      cutOff: "توقفت: حلّ تصحيحك محلّها",
      readCutOff: "توقفت القراءة بصوت عالٍ عندما بدأت بالكلام",
      hint: "يمكنك إرسال تصحيح أثناء عمل الوكيل: يتوقف بعد الخطوة الحالية ويبدأ من جديد بتصحيحك.",
      done: "قبل تصحيحك كان الوكيل قد أنجز: {{steps}}.",
      changed: "هذه غيّرت شيئًا ولم يُتراجع عنها: {{steps}}. اطلب التراجع إذا أردت.",
    },
    problems: {
      title: "لم تنجح بعض الخطوات، لذا قد تكون الإجابة ناقصة:",
      denied: "لم يُسمح بـ «{{step}}».",
      failed: "لم تنجح «{{step}}».",
      rejected: "رفض المعتمِد «{{step}}».",
      comment: "تعليقه: {{comment}}",
    },
    claims: {
      title: "ملاحظة: تقول الإجابة إن شيئًا ما أُنجز، لكن لم يقم أي نظام بذلك في هذا الطلب:",
      case: "لم تُفتح أي حالة أو تذكرة فعلًا.",
      notify: "لم تُرسل أي رسالة أو إشعار فعلًا.",
      payment: "لم يُدفع أي مبلغ فعلًا.",
      access: "لم تُمنح أي صلاحية فعلًا.",
      booking: "لم يُحجز أي شيء فعلًا.",
    },
    runNotes: {
      limitWent: "بلغت «{{step}}» حدّها وهو {{limit}} مرات، فانتقل الوكيل إلى «{{to}}».",
      limitStopped: "بلغت «{{step}}» حدّها وهو {{limit}} مرات، فتوقف التشغيل واحتفظ بآخر إجابة.",
      simulated: "لم يكن الذكاء الاصطناعي متأكدًا (بنسبة {{pct}}). في الاختبار الآمن تابع باقتراحه: {{value}}.",
      retried: "لم تنجح «{{step}}»، فأُعيدت المحاولة (المحاولة {{attempt}}).",
      workingNotes: "ملاحظات العمل",
      workingNotesHelp: "ما كتبته الخطوات لبعضها أثناء العمل. لا يرى الشخص إلا الإجابة.",
    },
    subtitle: "اسأل وكيلًا؛ ترى كل خطوة يقوم بها والأنظمة التي يستخدمها وأي موافقة، لحظة حدوثها.",
    agent: "الوكيل",
    conversation: "المحادثة",
    empty: "لا توجد رسائل بعد. اكتب سؤالك أدناه للبدء.",
    input: "الرسالة",
    placeholder: "اكتب سؤالك… (Enter للإرسال، Shift+Enter لسطر جديد)",
    send: "إرسال",
    newConversation: "محادثة جديدة",
    download: "تنزيل {{name}}",
    newConversationHint: "ابدأ من جديد: ينسى الوكيل الأسئلة السابقة والمستندات المرفقة في هذه المحادثة.",
    remembers_zero: "لا يتذكر الوكيل أسئلة سابقة في هذه المحادثة.",
    remembers_one: "يتذكر الوكيل سؤالًا سابقًا واحدًا في هذه المحادثة.",
    remembers_two: "يتذكر الوكيل سؤالين سابقين في هذه المحادثة.",
    remembers_few: "يتذكر الوكيل {{count}} أسئلة سابقة في هذه المحادثة.",
    remembers_many: "يتذكر الوكيل {{count}} سؤالًا سابقًا في هذه المحادثة.",
    remembers_other: "يتذكر الوكيل {{count}} سؤال سابق في هذه المحادثة.",
    cancel: "إيقاف التشغيل",
    runError: "فشل التشغيل",
    role: { user: "أنت", assistant: "الوكيل" },
    status: { idle: "جاهز", running: "قيد التشغيل", awaiting_approval: "بانتظار الاعتماد", awaiting_input: "بانتظار إجابتك", finished: "مكتمل", error: "فشل", cancelled: "ملغى" },
    classification: { public: "عام", internal: "داخلي", confidential: "سري", restricted: "مقيد" },
    upload: {
      button: "إرفاق مستند",
      attached: "المستندات المرفقة",
      done: "تم رفع {{name}} وتصنيفه",
      failed: "فشل الرفع",
      processing: "قيد التحليل…",
      hint: "يمكنك إرفاق PDF أو Word أو Excel أو صفحات ويب أو نصوص أو صور (PNG وJPEG وTIFF)، حتى 25 ميغابايت.",
      remove: "إزالة {{name}}",
      kind: "نوع المستند {{name}} (غيّره إن كان خاطئاً)",
      willRead: "سيقرؤه الوكيل",
      read_zero: "قرأه الوكيل (بلا صفحات)",
      read_one: "قرأه الوكيل (صفحة واحدة)",
      read_two: "قرأه الوكيل (صفحتان)",
      read_few: "قرأه الوكيل ({{count}} صفحات)",
      read_many: "قرأه الوكيل ({{count}} صفحة)",
      read_other: "قرأه الوكيل ({{count}} صفحة)",
      readPartly: "قُرئت أول {{read}} من {{pages}} صفحة فقط",
      notRead: "تعذّر على الوكيل قراءة {{name}}",
      refused: {
        too_sensitive: "لم يُقرأ: أكثر حساسية مما يُسمح لهذا الوكيل",
        not_found: "لم يُقرأ: لم يُعثر على المستند",
        unreadable: "لم يُقرأ: تعذّر استخراج نص منه",
        processing: "لم يُقرأ بعد: ما زال قيد التحليل. اسأل مرة أخرى بعد قليل.",
      },
      reason: {
        too_large: "حجم {{name}} أكبر من 25 ميغابايت.",
        unsupported: "{{name}}: هذا النوع من الملفات غير مقبول.",
        encrypted: "{{name}} محمي بكلمة مرور. أرفق نسخة بلا كلمة مرور.",
        unsafe: "يحتوي {{name}} على وحدات ماكرو أو محتوى نشط فرُفض.",
        empty: "{{name}} فارغ.",
        unreadable: "تعذرت قراءة {{name}}. تأكد أن الملف غير تالف.",
        network: "تعذر إرسال {{name}}: تحقق من الاتصال وحاول مجدداً.",
        failed: "تعذر رفع {{name}}. حاول مجدداً.",
      },
    },
    docLabel: { invoice: "فاتورة", purchase_order: "أمر شراء", contract: "عقد", id_document: "وثيقة هوية", other: "أخرى" },
    activity: {
      title: "النشاط",
      empty: "تظهر خطوات الوكيل والأنظمة التي يستخدمها هنا أثناء عمله.",
      steps: "الخطوات",
      tools: "الأنظمة المستخدمة",
      noTools: "لم يُستخدم أي نظام بعد",
      node: "الخطوة: {{node}}",
      readDocument_zero: "قرأ المستند المرفق (بلا صفحات)",
      readDocument_one: "قرأ المستند المرفق (صفحة واحدة)",
      readDocument_two: "قرأ المستند المرفق (صفحتان)",
      readDocument_few: "قرأ المستند المرفق ({{count}} صفحات)",
      readDocument_many: "قرأ المستند المرفق ({{count}} صفحة)",
      readDocument_other: "قرأ المستند المرفق ({{count}} صفحة)",
      readDocumentPartly: "قرأ المستند المرفق: أول {{read}} من {{pages}} صفحة فقط",
      usedConversation_zero: "لم يستخدم أسئلة سابقة من المحادثة",
      usedConversation_one: "استخدم سؤالًا سابقًا واحدًا من المحادثة",
      usedConversation_two: "استخدم سؤالين سابقين من المحادثة",
      usedConversation_few: "استخدم {{count}} أسئلة سابقة من المحادثة",
      usedConversation_many: "استخدم {{count}} سؤالًا سابقًا من المحادثة",
      usedConversation_other: "استخدم {{count}} سؤال سابق من المحادثة",
    },
    steps: { working: "يعمل", retrieve: "البحث في المعرفة", think: "التفكير", lookup: "الاستعلام عن السجلات", approve: "بانتظار الاعتماد", pay: "جدولة الدفع", done: "انتهى" },
    citations: { title: "المصادر", empty: "لم تُسترجع مصادر في هذا التشغيل.", source: "المصدر: {{source}}", score: "الصلة: {{score}}", uri: "موقع المصدر" },
    approval: {
      title: "مطلوب اعتماد",
      answered: "أُرسلت الإجابة",
      args: "معاملات الأداة المقترحة",
      comment: "تعليق (مطلوب للرفض)",
      approve: "اعتماد",
      reject: "رفض",
      open: "فتح في صندوق المراجعة",
      waiting: "بانتظار المعتمِد. سيظهر القرار هنا.",
      approved: "تم الاعتماد والتوقيع",
      rejected: "تم الرفض والتوقيع",
      status: { pending: "معلّق", decided: "تم القرار", invalid_signature: "توقيع غير صالح" },
      error: {
        validation: "تحقق من النموذج: {{message}}",
        sign: "تعذر توقيع الاعتماد: {{message}}",
        decision: "تم التوقيع ولم يُطبق بعد: {{message}}. أعد المحاولة من صندوق المراجعة.",
      },
    },
    memory: {
      open: "الذاكرة",
      title: "ذاكرتي",
      consent: "السماح لـ {{agent}} بتذكري",
      consentHelp: "بدون موافقتك لا يحتفظ الوكيل بأي شيء عنك بعد التشغيل. سحب الموافقة يوقف الذكريات الجديدة، ويمكنك أيضًا حذف الموجودة.",
      consentGranted: "تم منح الموافقة",
      consentRevoked: "تم سحب الموافقة",
      export: "تصدير",
      deleteAll: "حذف الكل",
      deleteAllConfirm: "حذف كل الذكريات المتعلقة بك؟ لا يمكن التراجع.",
      deleteConfirm: "حذف هذه الذكرى؟",
      deleteOne: "حذف الذكرى",
      delete: "حذف",
      cancel: "إلغاء",
      empty: "لا توجد ذكريات محفوظة.",
      expires: "تنتهي في {{date}}",
      kind: { working: "من هذه المحادثة", episodic: "من محادثات سابقة", semantic: "معلومة عنك", procedural: "طريقتك المفضلة في إنجاز الأمور" },
      listTitle: "ما أتذكره عنك",
      anyAgent: "أي مساعد",
      exportCsv: "تنزيل كجدول (CSV)",
      exportJson: "تنزيل كل البيانات (JSON)",
      csv: { kind: "النوع", what: "ما يُتذكّر", agent: "المساعد", saved: "تاريخ الحفظ", expires: "تاريخ النسيان" },
    },
  },
};
